import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const COLORS_UPDATED_EVENT = 'ibx_colors_updated';

export type SectionBackgroundMode = 'default' | 'solid' | 'overlay' | 'gradient';
export type GradientDirection = 'to right' | 'to bottom' | '135deg';

export type SectionBackgroundConfig = {
  mode: SectionBackgroundMode;
  solidColor?: string;
  overlayColor?: string;
  overlayOpacity?: number;
  gradientFrom?: string;
  gradientTo?: string;
  gradientDirection?: GradientDirection;
  gradientOpacity?: number;
  isActive?: boolean; // ✅ NOVO
};

export type SectionColors = Record<string, SectionBackgroundConfig>;

export interface SectionDef {
  id: string;
  label: string;
  group: string;
}

export const SECTIONS: SectionDef[] = [
  { id: 'alerta', label: 'Alerta de Contratação', group: 'Problema' },
  { id: 'stats', label: 'Estatísticas', group: 'Problema' },
  { id: 'problem', label: 'Seção Problema', group: 'Problema' },
  { id: 'riscos', label: 'Riscos sem Assessoria', group: 'Problema' },
  { id: 'bad-insurance', label: 'Seguros Mal Contratados', group: 'Problema' },
  { id: 'perda-financeira', label: 'Perda Financeira', group: 'Problema' },
  { id: 'falsa-protecao', label: 'Falsa Proteção Empresarial', group: 'Problema' },

  { id: 'profissionalizacao', label: 'Profissionalização', group: 'Solução' },
  { id: 'solucao-ibx', label: 'Solução IBX Brasil', group: 'Solução' },
  { id: 'impacto-ibx', label: 'Impacto IBX Brasil', group: 'Solução' },
  { id: 'custo-sem-assessoria', label: 'Custo sem Assessoria', group: 'Solução' },
  { id: 'patrimonio', label: 'Patrimônio Mal Protegido', group: 'Solução' },
  { id: 'pagando-caro', label: 'Pagando Caro sem Perceber', group: 'Solução' },

  { id: 'metodo', label: 'Método de Contratação Ativa', group: 'Método' },
  { id: 'diferenciais', label: 'Diferenciais do Método', group: 'Método' },
  { id: 'beneficios', label: 'Benefícios do Método', group: 'Método' },
  { id: 'autor-metodo', label: 'Autor do Método', group: 'Método' },

  { id: 'solution', label: 'Nossa Solução', group: 'Serviços' },
  { id: 'features', label: 'Funcionalidades', group: 'Serviços' },
  { id: 'services', label: 'Serviços', group: 'Serviços' },
  { id: 'niveis-suporte', label: 'Níveis de Suporte', group: 'Serviços' },

  { id: 'testimonials', label: 'Depoimentos', group: 'Social' },
  { id: 'about', label: 'Sobre Nós', group: 'Social' },
  { id: 'cta', label: 'Chamada para Ação (CTA)', group: 'Social' },
  { id: 'faq', label: 'Perguntas Frequentes', group: 'Social' },
  { id: 'blog', label: 'Blog', group: 'Social' },
  { id: 'footer', label: 'Rodapé (Footer)', group: 'Social' },
];

function normalizeConfig(value?: Partial<SectionBackgroundConfig>): SectionBackgroundConfig {
  return {
    mode: value?.mode || 'default',
    solidColor: value?.solidColor || '#ffffff',
    overlayColor: value?.overlayColor || '#0f172a',
    overlayOpacity: value?.overlayOpacity ?? 0.2,
    gradientFrom: value?.gradientFrom || '#ffffff',
    gradientTo: value?.gradientTo || '#f8fafc',
    gradientDirection: value?.gradientDirection || 'to right',
    gradientOpacity: value?.gradientOpacity ?? 1,
     isActive: value?.isActive ?? true,
  };
}

async function fetchSectionColorsFromDb(): Promise<SectionColors> {
  const { data, error } = await supabase
    .from('section_styles')
    .select('id, config');

  if (error) {
    console.error('Erro ao buscar estilos das seções:', error);
    return {};
  }

  const colors: SectionColors = {};

  for (const row of data || []) {
    colors[row.id] = normalizeConfig((row.config || {}) as Partial<SectionBackgroundConfig>);
  }

  return colors;
}

export function useSectionColors(): [
  SectionColors,
  (id: string, config: SectionBackgroundConfig) => Promise<void>,
  (id: string) => Promise<void>,
  () => Promise<void>,
] {
  const [colors, setColors] = useState<SectionColors>({});

  const reload = useCallback(async () => {
    const next = await fetchSectionColorsFromDb();
    setColors(next);
  }, []);

  useEffect(() => {
    void reload();

    const channel = supabase
      .channel('section-styles-realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'section_styles' },
        async () => {
          const next = await fetchSectionColorsFromDb();
          setColors(next);
          window.dispatchEvent(new Event(COLORS_UPDATED_EVENT));
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [reload]);

  const setColor = useCallback(async (id: string, config: SectionBackgroundConfig) => {
    const normalized = normalizeConfig(config);

    const { error } = await supabase
      .from('section_styles')
      .upsert(
        {
          id,
          config: normalized,
        },
        { onConflict: 'id' }
      );

    if (error) {
      console.error('Erro ao salvar estilo da seção:', error);
      return;
    }

    setColors((prev) => ({
      ...prev,
      [id]: normalized,
    }));

    window.dispatchEvent(new Event(COLORS_UPDATED_EVENT));
  }, []);

  const resetColor = useCallback(async (id: string) => {
    const { error } = await supabase
      .from('section_styles')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Erro ao resetar estilo da seção:', error);
      return;
    }

    setColors((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

    window.dispatchEvent(new Event(COLORS_UPDATED_EVENT));
  }, []);

  const resetAll = useCallback(async () => {
    const { error } = await supabase
      .from('section_styles')
      .delete()
      .neq('id', '');

    if (error) {
      console.error('Erro ao resetar todos os estilos:', error);
      return;
    }

    setColors({});
    window.dispatchEvent(new Event(COLORS_UPDATED_EVENT));
  }, []);

  return [colors, setColor, resetColor, resetAll];
}